import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  FolderOpen, 
  FileText, 
  Download, 
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Package,
  Truck,
  ShoppingCart,
  MapPin,
  Users,
  TrendingUp,
  Calendar,
  RefreshCw,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { 
  Container, 
  Row, 
  Col, 
  Button, 
  Card, 
  Table, 
  Pagination, 
  InputGroup, 
  Form, 
  Badge, 
  Spinner, 
  Alert,
  Modal,
  ProgressBar,
  Accordion,
  Tabs,
  Tab
} from 'react-bootstrap';
import { apiService } from '../services/api';
import { deliveriesLoadStatusBadgeProps, loadStatusDisplayAsStored } from '../constants/logisticsDisplay';
import './Dashboard.css';

const ProjectReports = () => {
  // State management
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectReport, setProjectReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination states
  const [companiesPagination, setCompaniesPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  const [projectsPagination, setProjectsPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });

  // Search and filter states
  const [companiesSearch, setCompaniesSearch] = useState('');
  const [projectsSearch, setProjectsSearch] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  // Modal states
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Load companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch companies with pagination and search
  const fetchCompanies = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching companies with params:', { page, limit: companiesPagination.limit, search });
      const response = await apiService.getCompaniesForReporting(page, companiesPagination.limit, search);
      console.log('Companies response:', response);
      
      if (response.success) {
        setCompanies(response.data);
        setCompaniesPagination({
          page: response.page,
          totalPages: response.totalPages,
          totalCount: response.totalCount,
          limit: response.limit
        });
      } else {
        setError('Failed to fetch companies');
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(`Failed to fetch companies: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects for selected company
  const fetchProjects = async (companyId, page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getProjectsByCompanyForReporting(companyId, page, projectsPagination.limit, search);
      
      if (response.success) {
        setProjects(response.data);
        setProjectsPagination({
          page: response.page,
          totalPages: response.totalPages,
          totalCount: response.totalCount,
          limit: response.limit
        });
      } else {
        setError('Failed to fetch projects');
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // Generate project report
  const generateProjectReport = async (projectId) => {
    try {
      setGeneratingReport(true);
      setError('');
      
      const response = await apiService.generateProjectReport(projectId);
      
      if (response.success) {
        setProjectReport(response.report);
        setShowReportModal(true);
        setSuccess('Report generated successfully');
      } else {
        setError('Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Handle company selection
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setProjects([]);
    setSelectedProject(null);
    setProjectReport(null);
    fetchProjects(company.id);
  };

  // Handle project selection
  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  // Toggle company expansion
  const toggleCompanyExpansion = (companyId) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  // Handle pagination
  const handleCompaniesPageChange = (page) => {
    fetchCompanies(page, companiesSearch);
  };

  const handleProjectsPageChange = (page) => {
    if (selectedCompany) {
      fetchProjects(selectedCompany.id, page, projectsSearch);
    }
  };

  // Handle search
  const handleCompaniesSearch = (search) => {
    setCompaniesSearch(search);
    fetchCompanies(1, search);
  };

  const handleProjectsSearch = (search) => {
    setProjectsSearch(search);
    if (selectedCompany) {
      fetchProjects(selectedCompany.id, 1, search);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': { variant: 'success', icon: CheckCircle },
      'In Progress': { variant: 'primary', icon: Clock },
      'Planning': { variant: 'warning', icon: Calendar },
      'Completed': { variant: 'success', icon: CheckCircle },
      'On Hold': { variant: 'secondary', icon: AlertCircle },
      'Cancelled': { variant: 'danger', icon: AlertCircle }
    };

    const config = statusConfig[status] || { variant: 'secondary', icon: Clock };
    const IconComponent = config.icon;

    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        <IconComponent size={12} />
        {loadStatusDisplayAsStored(status)}
      </Badge>
    );
  };

  /** Loads.Status — In Transit (same as logistics / Deliveries). */
  const getLoadStatusBadge = (status) => {
    const { className, ...rest } = deliveriesLoadStatusBadgeProps(status);
    const mergedClass = ['d-flex', 'align-items-center', 'gap-1', className].filter(Boolean).join(' ') || undefined;
    return (
      <Badge {...rest} className={mergedClass}>
        <Truck size={12} />
        {loadStatusDisplayAsStored(status)}
      </Badge>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format weight
  const formatWeight = (weight) => {
    return `${parseFloat(weight).toFixed(2)} lbs`;
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <div className="icon-wrapper bg-primary">
              <BarChart3 size={24} />
            </div>
            <div>
              <h2 className="mb-0">Project Reports</h2>
              <p className="text-muted mb-0">Comprehensive project insights and analytics</p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <AlertCircle size={16} className="me-2" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          <CheckCircle size={16} className="me-2" />
          {success}
        </Alert>
      )}

      <Row>
        {/* Companies List */}
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Building2 size={20} />
                <h5 className="mb-0">Companies</h5>
              </div>
              <Badge bg="secondary">{companiesPagination.totalCount} total</Badge>
            </Card.Header>
            <Card.Body>
              {/* Search */}
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search companies..."
                  value={companiesSearch}
                  onChange={(e) => handleCompaniesSearch(e.target.value)}
                />
              </InputGroup>

              {/* Companies Table */}
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2 text-muted">Loading companies...</p>
                </div>
              ) : (
                <>
                  <Table responsive className="modern-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Location</th>
                        <th>Projects</th>
                        <th>Users</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((company) => (
                        <React.Fragment key={company.id}>
                          <tr 
                            className="cursor-pointer"
                            onClick={() => handleCompanySelect(company)}
                          >
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCompanyExpansion(company.id);
                                  }}
                                >
                                  {expandedCompanies.has(company.id) ? 
                                    <ChevronDown size={16} /> : 
                                    <ChevronRight size={16} />
                                  }
                                </Button>
                                <div>
                                  <div className="fw-bold">{company.company}</div>
                                  <small className="text-muted">{company.branch}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="small">
                                <div>{company.coCity}, {company.coState}</div>
                                <div className="text-muted">{company.coZip}</div>
                              </div>
                            </td>
                            <td>
                              <Badge bg="primary">{company.projectCount}</Badge>
                            </td>
                            <td>
                              <Badge bg="secondary">{company.userCount}</Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompanySelect(company);
                                }}
                              >
                                <Eye size={14} />
                              </Button>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {companiesPagination.totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.Prev 
                          disabled={companiesPagination.page === 1}
                          onClick={() => handleCompaniesPageChange(companiesPagination.page - 1)}
                        />
                        {Array.from({ length: companiesPagination.totalPages }, (_, i) => i + 1).map(page => (
                          <Pagination.Item
                            key={page}
                            active={page === companiesPagination.page}
                            onClick={() => handleCompaniesPageChange(page)}
                          >
                            {page}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next 
                          disabled={companiesPagination.page === companiesPagination.totalPages}
                          onClick={() => handleCompaniesPageChange(companiesPagination.page + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Projects List */}
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <FolderOpen size={20} />
                <h5 className="mb-0">
                  {selectedCompany ? `${selectedCompany.company} Projects` : 'Projects'}
                </h5>
              </div>
              {selectedCompany && (
                <Badge bg="secondary">{projectsPagination.totalCount} total</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {selectedCompany ? (
                <>
                  {/* Search */}
                  <InputGroup className="mb-3">
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search projects..."
                      value={projectsSearch}
                      onChange={(e) => handleProjectsSearch(e.target.value)}
                    />
                  </InputGroup>

                  {/* Projects Table */}
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" />
                      <p className="mt-2 text-muted">Loading projects...</p>
                    </div>
                  ) : (
                    <>
                      <Table responsive className="modern-table">
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Status</th>
                            <th>Releases</th>
                            <th>Loads</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projects.map((project) => (
                            <tr 
                              key={project.id}
                              className={`cursor-pointer ${selectedProject?.id === project.id ? 'table-active' : ''}`}
                              onClick={() => handleProjectSelect(project)}
                            >
                              <td>
                                <div>
                                  <div className="fw-bold">{project.projectName}</div>
                                  <small className="text-muted">{project.projectNumber}</small>
                                </div>
                              </td>
                              <td>{getStatusBadge(project.status)}</td>
                              <td>
                                <Badge bg="info">{project.releaseCount}</Badge>
                              </td>
                              <td>
                                <Badge bg="warning">{project.loadCount}</Badge>
                              </td>
                              <td>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateProjectReport(project.id);
                                  }}
                                  disabled={generatingReport}
                                >
                                  {generatingReport ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <FileText size={14} />
                                  )}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      {/* Pagination */}
                      {projectsPagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <Pagination.Prev 
                              disabled={projectsPagination.page === 1}
                              onClick={() => handleProjectsPageChange(projectsPagination.page - 1)}
                            />
                            {Array.from({ length: projectsPagination.totalPages }, (_, i) => i + 1).map(page => (
                              <Pagination.Item
                                key={page}
                                active={page === projectsPagination.page}
                                onClick={() => handleProjectsPageChange(page)}
                              >
                                {page}
                              </Pagination.Item>
                            ))}
                            <Pagination.Next 
                              disabled={projectsPagination.page === projectsPagination.totalPages}
                              onClick={() => handleProjectsPageChange(projectsPagination.page + 1)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <FolderOpen size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">Select a Company</h5>
                  <p className="text-muted">Choose a company from the list to view its projects</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Project Report Modal */}
      <Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FileText size={20} className="me-2" />
            Project Report: {projectReport?.project?.projectName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {projectReport && (
            <Tabs defaultActiveKey="summary" className="mb-3">
              {/* Summary Tab */}
              <Tab eventKey="summary" title="Summary">
                <Row className="mb-4">
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body>
                        <Package size={32} className="text-primary mb-2" />
                        <h4>{projectReport.summary.totalReleases}</h4>
                        <p className="text-muted mb-0">Total Releases</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body>
                        <Truck size={32} className="text-warning mb-2" />
                        <h4>{projectReport.summary.totalLoads}</h4>
                        <p className="text-muted mb-0">Total Loads</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body>
                        <ShoppingCart size={32} className="text-info mb-2" />
                        <h4>{projectReport.summary.totalRetailQuotes}</h4>
                        <p className="text-muted mb-0">Retail Quotes</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body>
                        <MapPin size={32} className="text-success mb-2" />
                        <h4>{projectReport.summary.totalSites}</h4>
                        <p className="text-muted mb-0">Project Sites</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Card>
                      <Card.Header>
                        <h6 className="mb-0">Weight Summary</h6>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Total Weight:</span>
                          <strong>{formatWeight(projectReport.summary.totalWeight)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Placed Weight:</span>
                          <strong>{formatWeight(projectReport.summary.placedWeight)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Remaining Weight:</span>
                          <strong>{formatWeight(projectReport.summary.remainingWeight)}</strong>
                        </div>
                        <ProgressBar 
                          now={(parseFloat(projectReport.summary.placedWeight) / parseFloat(projectReport.summary.totalWeight)) * 100}
                          className="mt-3"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card>
                      <Card.Header>
                        <h6 className="mb-0">Financial Summary</h6>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <span>Total Retail Amount:</span>
                          <strong>{formatCurrency(projectReport.summary.totalRetailAmount)}</strong>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Releases Tab */}
              <Tab eventKey="releases" title="Releases">
                <Row className="mb-3">
                  <Col>
                    <h6>Release Status Summary</h6>
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Count</th>
                          <th>Total Weight</th>
                          <th>Placed Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectReport.releases.summary.map((release, index) => (
                          <tr key={index}>
                            <td>{getStatusBadge(release.status)}</td>
                            <td>{release.count}</td>
                            <td>{formatWeight(release.totalWeight)}</td>
                            <td>{formatWeight(release.placedWeight)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h6>Recent Releases</h6>
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th>Release</th>
                          <th>Weight</th>
                          <th>Status</th>
                          <th>Firmed Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectReport.releases.recent.map((release) => (
                          <tr key={release.id}>
                            <td>{release.release}</td>
                            <td>{formatWeight(release.weight)}</td>
                            <td>{getStatusBadge(release.status)}</td>
                            <td>{release.firmedDate ? new Date(release.firmedDate).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Tab>

              {/* Loads Tab */}
              <Tab eventKey="loads" title="Loads">
                <Row className="mb-3">
                  <Col>
                    <h6>Load Status Summary</h6>
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectReport.loads.summary.map((load, index) => (
                          <tr key={index}>
                            <td>{getLoadStatusBadge(load.status)}</td>
                            <td>{load.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h6>Recent Loads</h6>
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th>Load No</th>
                          <th>Status</th>
                          <th>Delivery Date</th>
                          <th>Ship Via</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectReport.loads.recent.map((load) => (
                          <tr key={load.id}>
                            <td>{load.loadNo}</td>
                            <td>{getLoadStatusBadge(load.status)}</td>
                            <td>{load.deliveryDate ? new Date(load.deliveryDate).toLocaleDateString() : '-'}</td>
                            <td>{load.shipVia}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Tab>

              {/* Sites Tab */}
              <Tab eventKey="sites" title="Project Sites">
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Site Annotation</th>
                      <th>Description</th>
                      <th>Contact</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectReport.sites.map((site) => (
                      <tr key={site.id}>
                        <td>{site.siteAnnotation}</td>
                        <td>{site.description}</td>
                        <td>
                          <div>
                            <div>{site.siteContactName}</div>
                            <small className="text-muted">{site.siteContactPhone}</small>
                          </div>
                        </td>
                        <td>
                          {site.latitude && site.longitude ? (
                            <small>{site.latitude}, {site.longitude}</small>
                          ) : (
                            <small className="text-muted">No coordinates</small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Close
          </Button>
          <Button variant="primary">
            <Download size={16} className="me-2" />
            Export Report
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjectReports;
